// Regras de negócio do funil (PLANO 4.3). Vivem separadas do acesso a dados
// porque a mesma verificação roda em dois lugares: aqui, para dar uma mensagem
// clara antes de gravar, e nas security rules, que são a autoridade final.
//
// V4: negociador. `compareceu` e `em_negociacao` exigem um negociador na
// MUDANÇA MANUAL de etapa (select, arraste, lista).
import { ETAPAS_POS_COMPARECIMENTO } from '@/leads/cadencia';

/**
 * ★★★ WO lote 3c (D3) — AS TRÊS ETAPAS DE NEGOCIAÇÃO, e duas coisas mudaram aqui.
 *
 * **1. `fechado` ENTROU.** A lista era `ETAPAS_POS_COMPARECIMENTO` (`compareceu`,
 * `nao_fechou`) e deixava justamente a etapa que credita **VALOR** sem dono declarado.
 *
 * **2. ⚠⚠ A RESSALVA DA AGENDA SAIU, e ela era uma decisão MEDIDA — reverter exige dizer
 * por quê.** O comentário anterior dizia:
 *
 *   > *"Só no cliente, de propósito: o comparecimento registrado pela Agenda pode chegar
 *   > sem negociador (a tarefa vai para o SDR e o painel cobra a escolha), então as rules
 *   > não impõem."*
 *
 * ★ E o diálogo da baixa registrava o número: *"a medição dos 223 de 227 leads sem
 * negociador. Exigi-lo aqui travaria quase toda baixa."*
 *
 * ⚠ Aquela medição é sobre o PASSADO — leads que já estavam sem negociador. ★ O que o D3
 * cobra é o FUTURO: a partir de agora, quem dá a baixa escolhe quem negociou. Medido hoje,
 * **960 dos 999** leads nessas etapas não têm negociador; é precisamente esse número que a
 * regra existe para parar de crescer.
 *
 * ★★ E as rules PASSAM A IMPOR — é o que torna a trava real. A exigência no cliente
 * sozinha nunca foi garantia; era aviso.
 */
export const ETAPAS_COM_NEGOCIADOR = [...ETAPAS_POS_COMPARECIMENTO, 'fechado'];

/**
 * ★★ AS ETAPAS QUE AINDA ESPERAM UM RETORNO — e NÃO são as mesmas de cima.
 *
 * ⚠⚠ Até este lote as duas perguntas usavam a MESMA lista, porque as respostas coincidiam.
 * ★ Ao pôr `fechado` em `ETAPAS_COM_NEGOCIADOR`, o badge de retorno passou a aparecer em
 * lead FECHADO — e lead fechado não espera retorno nenhum. **Três testes caíram e pegaram.**
 *
 * ★ São perguntas diferentes e agora têm listas diferentes:
 *   • `ETAPAS_COM_NEGOCIADOR` — *"quem negociou isto?"* → inclui `fechado`;
 *   • `ETAPAS_QUE_ESPERAM_RETORNO` — *"ainda falta voltar a falar?"* → **não** inclui.
 *
 * ⚠ Um alias que serve a dois donos é uma coincidência esperando para virar defeito.
 */
export const ETAPAS_QUE_ESPERAM_RETORNO = ETAPAS_POS_COMPARECIMENTO;

/**
 * ★ VJ — as duas colunas de "não" EXIGEM uma DATA DE RETORNO, salvo quando o
 * motivo escolhido ENCERRA (Candidato, Fora da região, Descarte, Não aguardou…):
 * lead encerrado não tem próximo contato. A data vira uma TAREFA (o "próximo
 * contato" que já existe — Próximos contatos, painel), no mesmo lote da etapa.
 * `encerra` chega decidido pela tela a partir da lista da clínica
 * (`leads/motivos.js`) — esta função não lê config, de propósito.
 */
/**
 * ★★ WI (29/set/2026) — `nao_compareceu` ENTROU NA LISTA. A regra é UMA SÓ.
 *
 * Palavras do dono: *"quando a gente colocar que a pessoa faltou, a gente tem que colocar
 * para gerar um novo contato, obrigatoriamente"* — e ele acrescentou o porquê: *"senão vai
 * ficar cliente perdido"*.
 *
 * ★ E não era teoria. Medido em `docs/wi-medicao.md` §3: das **13** baixas "faltou" que uma
 * pessoa apertou em produção, **10** deixaram o lead sem nenhuma tarefa aberta; refinando
 * por lead distinto, **7 de 9** ficaram sem tarefa **e** sem consulta futura — perdidos de
 * verdade. E **554 dos 556** leads em `nao_compareceu` não têm próximo contato nenhum.
 *
 * ★★ ENTROU PARA OS DOIS CAMINHOS, de propósito: a baixa da agenda E o card movido no
 * funil. Meia regra reproduziria a divergência "a agenda tem uma regra, o funil tem outra",
 * que é exatamente o defeito que esta peça veio fechar — e os 554 entraram pela porta do
 * funil.
 */
export const ETAPAS_COM_RETORNO = ['nao_agendou', 'nao_fechou', 'nao_compareceu'];

export const exigeDataDeRetorno = ({ etapa, encerra } = {}) =>
  ETAPAS_COM_RETORNO.includes(etapa) && encerra !== true;

/** Date válido, Timestamp do Firestore, ou nada. */
export const dataValida = (v) => {
  const d = typeof v?.toDate === 'function' ? v.toDate() : v instanceof Date ? v : null;
  return d !== null && !Number.isNaN(d.getTime());
};

const rotulo = { compareceu: 'Compareceu', nao_fechou: 'Não fechou' };

/**
 * Devolve a mensagem de erro, ou null se a mudança de etapa é válida.
 *
 * ★ VG: as duas colunas de "não" EXIGEM motivo da lista (`motivoNaoAgendou` /
 * `motivoNaoFechou`), e "Não fechou" exige também o negociador — é ele quem
 * preenche. `perdido` não existe mais como etapa.
 */
/**
 * ★★ WH (29/set/2026) — ENTRAR EM "COMPARECEU" EXIGE CÓDIGO, NOME CONFIRMADO E VALOR.
 *
 * ★ POR QUE NA PORTA, e não como aviso no card. Medido em `docs/wh-medicao.md`: dos
 * 59 comparecimentos de setembro, **25 sem código**, **38 sem valor** e **47 sem
 * negociador** — e o negociador **já era obrigatório aqui** desde a V4. "Deixar
 * passar e cobrar depois pelo badge" está escrito no código e **medidamente não
 * funcionou**; havia paciente no funil chamado `🎼`.
 *
 * ★ A CAIXA "sem orçamento" dispensa **só o valor** (decisão do dono): quem
 * compareceu tem ficha no sistema da clínica, então código e nome seguem valendo.
 * Ela só conta `=== true` — string `'true'` ou `1` não passam, para um payload
 * malformado não abrir a porta por acidente.
 *
 * ★ O NOME é um GESTO, não um julgamento. A medição foi explícita: não dá para
 * saber se um nome está "atualizado" (`Laura` é legítimo, `🎼` não). O que se exige
 * é a CONFIRMAÇÃO (`nomeConfirmado`) — um ato, registrável — mais o nome não vazio,
 * porque confirmar o vazio não vale nada.
 *
 * ★ Nenhum papel entra aqui, de propósito: é a garantia estrutural de que a regra
 * vale para todo mundo, inclusive o gestor (decisão 3). E a função olha a etapa de
 * DESTINO, o que faz o recuo `nao_fechou → compareceu` exigir o mesmo (decisão 8).
 */
/**
 * ★★ WI — A VERIFICAÇÃO DOS DADOS DO COMPARECIMENTO, EXPORTADA. Uma definição, dois usos.
 *
 * `validarEtapa('compareceu', …)` exige os dados **e o NEGOCIADOR** (V4). Na BAIXA pela
 * agenda/ficha o negociador NÃO é exigido, e isso é decisão antiga e deliberada — está no
 * cabeçalho deste arquivo: *"o comparecimento registrado pela Agenda pode chegar sem
 * negociador (a tarefa vai para o SDR e o painel cobra a escolha)"*.
 *
 * ★ Medido antes de escolher (`docs/wi-execucao.md` §5): **223 dos 227** leads em
 * `compareceu` não têm negociador. Exigi-lo na baixa travaria praticamente toda baixa — o
 * incidente da WC de novo, e numa porta nova.
 *
 * ★ Por isso o diálogo da baixa chama ESTA função, e não `validarEtapa`: é a MESMA
 * verificação (uma definição só), sem a cláusula do negociador que não é dela.
 */
export const validarComparecimento = (dados = {}) => textoDoComparecimento(dados);

const textoDoComparecimento = (dados) => {
  if (!String(dados.codigoCareflow ?? '').trim()) {
    return 'Informe o código do paciente para mover o lead para Compareceu.';
  }
  if (dados.nomeConfirmado !== true || !String(dados.nome ?? '').trim()) {
    return 'Confirme o nome do paciente como está cadastrado na clínica.';
  }
  if (!(Number(dados.valorOrcado) > 0) && dados.semOrcamento !== true) {
    return 'Informe o valor da oportunidade, ou marque "compareceu sem orçamento".';
  }
  return null;
};

export function validarEtapa(etapa, dados = {}) {
  if (etapa === 'fechado' && !(Number(dados.valorFechado) > 0)) {
    return 'Informe o valor fechado para mover o lead para Fechou.';
  }
  if (etapa === 'compareceu') {
    const erro = textoDoComparecimento(dados);
    if (erro) return erro;
  }
  if (etapa === 'nao_agendou' && !dados.motivoNaoAgendou) {
    return 'Escolha o motivo de não ter agendado.';
  }
  if (etapa === 'nao_fechou' && !dados.motivoNaoFechou) {
    return 'Escolha o motivo de não ter fechado.';
  }
  if (ETAPAS_COM_NEGOCIADOR.includes(etapa) && !dados.negociadorId) {
    return `Escolha o negociador antes de mover o lead para ${rotulo[etapa] ?? etapa}.`;
  }
  // VJ: motivo vivo → data de retorno obrigatória; motivo que encerra → nada.
  if (exigeDataDeRetorno({ etapa, encerra: dados.encerra }) && !dataValida(dados.retornoEm)) {
    return 'Informe a data do retorno.';
  }
  return null;
}

/** Etapas que SEMPRE exigem um campo extra e por isso pedem diálogo próprio. */
/**
 * Etapas que sempre pedem diálogo.
 *
 * `agendado` entrou na VF-1: mover para agendado passa a **marcar a consulta no
 * mesmo ato**. Antes a etapa era gravada sozinha e o agendamento vinha por outro
 * caminho (o painel do lead), o que produzia lead agendado sem consulta nenhuma.
 */
/**
 * ★★ WI — `nao_compareceu` ENTROU AQUI TAMBÉM, E NÃO É DETALHE: É O BECO SEM SAÍDA.
 *
 * Sem esta linha, `validarEtapa` passaria a exigir a data do retorno e `exigeCampoExtra`
 * NÃO abriria diálogo nenhum — a SDR arrastaria o card, leria *"Informe a data do retorno"*
 * e não teria onde digitar. É literalmente a mutação H7 da WH, que existe para matar
 * exatamente este erro. As duas linhas andam juntas ou nenhuma anda.
 */
export const ETAPAS_COM_CAMPO_OBRIGATORIO = ['fechado', 'agendado', 'nao_agendou', 'nao_fechou', 'nao_compareceu'];

/**
 * A mudança para `etapa` precisa de diálogo?
 *
 * Fechado e as duas de "não" SEMPRE (valor e motivo são exigidos pelas rules). As etapas de
 * negociação, quando **falta o negociador (V4) ou o valor orçado (VE)** — e só
 * então: quem já preencheu os dois move direto pelo select ou pelo arraste, sem
 * um diálogo repetido a cada passo do funil. Quem ainda não preencheu continua
 * sendo lembrado, que é o ponto.
 *
 * O comparecimento marcado pela AGENDA não passa por aqui: grava `valorOrcado: 0`
 * e o badge do card cobra depois — o orçamento sai da avaliação, e quem marca
 * "compareceu" às vezes ainda não sabe.
 */
export function exigeCampoExtra(etapa, lead = null) {
  if (ETAPAS_COM_CAMPO_OBRIGATORIO.includes(etapa)) return true;
  if (ETAPAS_COM_NEGOCIADOR.includes(etapa)) {
    // ★★ WH: o CÓDIGO entrou na lista do que faz o diálogo abrir. Sem isto a porta
    // (`validarEtapa`) recusaria a gravação **sem abrir a tela onde se digita** — a
    // SDR ficaria presa num beco sem saída. E a marca `semOrcamento` fecha o caso
    // do valor: quem já declarou que não houve orçamento não precisa ser perguntado
    // de novo a cada mudança de etapa.
    const faltaValor = !(Number(lead?.valorOrcado) > 0) && lead?.semOrcamento !== true;
    return !lead?.negociadorId || faltaValor || !String(lead?.codigoCareflow ?? '').trim();
  }
  return false;
}

// ------------------------------------------------- a etiqueta de CONTATO do card (V4; texto refeito na WE)

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  return null;
};

// ------------------------------------------------- importado sem atividade (VO)

/**
 * ★ VO — fim da janela em que a planilha 2026 foi importada em produção.
 *
 * ORIGEM: a importação da V7 (bloco M) rodou de 16/set/2026 19:51Z (degrau 2)
 * a 17/set/2026 16:23Z (fim do degrau 5), e o corretor de datas às 16:19Z. A
 * constante fica um pouco depois do último carimbo, com folga.
 *
 * POR QUE EXISTE: a importação gravou `atualizadoEm` = data da última linha da
 * planilha (para a ordenação fazer sentido), e os leads NOVOS não receberam
 * `importacao.em` — não há no documento uma marca de "quando entrou". Sem isso,
 * a única forma de saber se a equipe já mexeu num importado é comparar o
 * `atualizadoEm` com o fim da importação: qualquer ação da equipe pela tela
 * grava `atualizadoEm = serverTimestamp()`, que é sempre posterior.
 *
 * QUANDO SOME: a próxima clínica ganha `importacao.em` gravado pelo script de
 * importação, e esta constante desaparece (regra passa a ler `importacao.em`).
 * NÃO carimbar `importacao.em` nos 7.491 leads de produção — decisão do dono.
 */
export const IMPORTACAO_PLANILHA_2026_ATE = new Date('2026-09-17T19:30:00Z');

/**
 * ★ VO — o lead veio da planilha e a equipe ainda não o tocou no sistema.
 *
 * Um lead importado em `compareceu` sem contato registrado não está "sem
 * retorno há 552 dias" por atraso da equipe: a história dele é anterior ao
 * sistema. Enquanto ninguém mexer, as etiquetas de cobrança se calam; no
 * primeiro toque (tarefa concluída, tentativa de ligação, qualquer edição) o
 * lead volta a ser cobrado como os nativos.
 *
 * Verdadeiro quando, e só quando: há `importacao.fonte`; não há
 * `ultimoContatoEm` nem `ultimaTentativaEm`; e `atualizadoEm` não é posterior
 * a `ate`. Lead importado SEM `atualizadoEm` conta como sem atividade — a
 * importação sempre grava o campo; ausente, ninguém tocou. Lead nativo (sem
 * `importacao`) é SEMPRE falso: o alerta real nunca é escondido.
 *
 * A regra só LÊ. Nenhuma das três portas que a usam (badge de retorno,
 * cobrança de orçamento, "sem negociador") grava nada no lead.
 */
export function importadoSemAtividade(lead, ate = IMPORTACAO_PLANILHA_2026_ATE) {
  if (!lead?.importacao?.fonte) return false;
  if (paraData(lead.ultimoContatoEm) || paraData(lead.ultimaTentativaEm)) return false;
  const atualizadoEm = paraData(lead.atualizadoEm);
  if (atualizadoEm && atualizadoEm.getTime() > ate.getTime()) return false;
  return true;
}

/**
 * O badge só faz sentido para quem compareceu e ainda não fechou — e não para
 * o importado que a equipe ainda não tocou (VO).
 */
export const mostrarBadgeRetorno = (lead) =>
  ETAPAS_QUE_ESPERAM_RETORNO.includes(lead?.etapa) && !importadoSemAtividade(lead);

/**
 * Dias inteiros desde o último CONTATO REGISTRADO: a tarefa concluída mais recente (`ultimoContatoEm`,
 * gravado por `concluirTarefa`) ou a última tentativa de ligação (`ultimaTentativaEm`) — o que for mais
 * novo. **`null` quando não há contato registrado nenhum.**
 *
 * ★★ WE (c) — O FALLBACK PARA `atualizadoEm` FOI REMOVIDO, E ISSO É O CONSERTO.
 *
 * Antes, na falta dos dois campos, a função caía em `atualizadoEm` e devolvia um número. Mas
 * `atualizadoEm` é "quando alguém mexeu neste documento pela última vez" — muda com uma NOTA, com a
 * correção de um valor, com a troca do negociador. Nada disso é contato com o paciente.
 *
 * O efeito na tela era uma mentira: a lead Marta de Fatima Borges mostrava **"retorno hoje"** porque a
 * Jéssica tinha escrito uma nota nela minutos antes — medido em produção, com o carimbo do `atualizadoEm`
 * batendo com o da nota ao milissegundo (`docs/we-medicao.md` §4). E a NOTA não é o problema: ela sempre
 * gravou só a interação e o `atualizadoEm` (§5). Quem inventava significado era esta função.
 *
 * ★ `null` agora quer dizer "não sei", e quem traduz isso em texto é o card — com um estado PRÓPRIO
 * ("sem contato registrado"), não com um número. Medido no dia do conserto: **8 dos 11** leads que mostram
 * a etiqueta dependiam deste fallback.
 */
export function diasSemRetorno(lead, agora = new Date()) {
  const candidatas = [lead?.ultimoContatoEm, lead?.ultimaTentativaEm]
    .map(paraData)
    .filter(Boolean);
  if (candidatas.length === 0) return null;
  const base = new Date(Math.max(...candidatas.map((d) => d.getTime())));
  return Math.max(0, Math.floor((agora.getTime() - base.getTime()) / 86_400_000));
}

/** A partir de quantos dias SEM CONTATO o badge vira alerta. */
export const DIAS_ALERTA_RETORNO = 3;
